import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Map, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import polyUtil from 'polyline-encoded';
import { v4 } from 'uuid';
import turf from 'turf';
import smoothscroll from 'smoothscroll-polyfill';

import FullscreenControl from 'react-leaflet-fullscreen';
import 'react-leaflet-fullscreen/dist/styles.css';
import 'font-awesome/css/font-awesome.min.css';
import 'leaflet.locatecontrol/dist/L.Control.Locate.min.css';

import 'leaflet.locatecontrol';

import 'mapbox-gl-leaflet';
import 'mapbox-gl';

import { THEME } from '../../configs/global';

import { getTime, getTimeSec } from '../../helpers/Helpers';
import { getStopName, getLastUpdate } from './ServiceDetails';

import iconScrollTop from '../../assets/images/back_to_top.svg';

L.Icon.Default.imagePath =
  '//cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.1/images/';

smoothscroll.polyfill();

const boundsOptions = { padding: [30, 30] };

const getIconPathFromActiveTheme = (iconName) => {
  switch (THEME.NAME) {
    case 'Megabus':
      return `megabus/${iconName}`;
    case 'WCCE':
      return `wcce/${iconName}`;  
    case 'NX-Bus':
      return `nxbus/${iconName}`;
    case 'Trailways':
      return `trailways/${iconName}`;
    case 'Suttonparkbus':
      return `suttonparkbus/${iconName}`;

    default:
      return iconName;
  }
};

const createIcon = (name, size, format = 'png', anchor, popupanchor) =>
  new L.Icon({
    iconUrl: require(`../../assets/images/${name}.${format}`),
    iconRetinaUrl: require(`../../assets/images/${name}.${format}`),
    iconSize: size,
    iconAnchor: anchor ? anchor : null,
    popupAnchor: popupanchor ? popupanchor : [0, 0],
  });

const iconBigSize = [24, 40];
const iconDefaultSize = [14, 26];
const iconSmallSize = [15, 15];

const mapStopsToMarkers = (stops) => {
  const stopsLen = stops.length;
  return stops.map((stop, index) => {
    const obj = {
      lat: stop.wgs84_latitude_degrees,
      lng: stop.wgs84_longitude_degrees,
      name: stop[THEME.PAGES.DETAILS.STOP]
        ? stop[THEME.PAGES.DETAILS.STOP]
        : stop.public_facing_name
        ? stop.public_facing_name
        : stop.name,
    };

    if (index === 0) {
      return {
        ...obj,
        icon: createIcon(
          getIconPathFromActiveTheme('stop-start'),
          iconBigSize,
          'svg',
          [12, 40],
          [1, -45]
        ),
      };
    }

    if (index + 1 === stopsLen) {
      return {
        ...obj,
        icon: createIcon(
          getIconPathFromActiveTheme('stop-finish'),
          iconBigSize,
          'svg',
          [12, 40],
          [1, -45]
        ),
      };
    }

    return {
      ...obj,
      icon: createIcon(
        getIconPathFromActiveTheme('stop'),
        iconDefaultSize,
        'svg',
        [7, 26],
        [0, -36]
      ),
    };
  });
};

const mapAlertsToMarkers = (alerts) => {
  if (alerts && alerts.length) {
    return alerts.map((alert) => ({
      lat: alert.wgs84_latitude_degrees,
      lng: alert.wgs84_longitude_degrees,
      name: alert.summary,
      cleared: alert.incident_is_finished,
      updated: alert.last_updated_time_formatted_local,
      timestamp: alert.created_time_formatted_local,
      description: alert.description,
      icon: createIcon(
        getIconPathFromActiveTheme(`traffic-${alert.incident_is_severe ? 'severe' : alert.incident_is_roadworks ? 'roadworks' : 'moderate'}`),
        [32, 28],
        'svg',
        [16, 28],
        [0, -27]
      ),
    }));
  }
  return [];
};

const mapStateToProps = (state) => ({
  services: state.service.services,
  alerts: state.trafficAlerts,
});

class MapWithProvider extends Map {
  createLeafletElement(props) {
    const LeafletMapElement = super.createLeafletElement(props);
    L.control.locate().addTo(LeafletMapElement);
    LeafletMapElement.on('viewreset', this.props.onZoom);
    LeafletMapElement.invalidateSize(
      LeafletMapElement.getBoundsZoom(
        this.props.fitbounds ? this.props.fitbounds : this.props.bounds
      )
    );

    L.mapboxGL({
      attribution:
        '<a href="https://www.maptiler.com/copyright/" target="_blank">© MapTiler</a> <a href="https://www.openstreetmap.org/copyright" target="_blank">© OpenStreetMap contributors</a>',
      accessToken:
        THEME.MAPTILER_KEY ? THEME.MAPTILER_KEY : 'pk.eyJ1IjoidXRyYWNrbWFwIiwiYSI6Im9iV245NnMifQ.ddr0eLkRuZUoNVglI8tZvg',
      style:
        'https://api.maptiler.com/maps/basic/style.json?key=' + (THEME.MAPSTYLE_KEY ? THEME.MAPSTYLE_KEY : 'Byaz3CMIZKoUmjEqSl1b'),
    }).addTo(LeafletMapElement);

    return LeafletMapElement;
  }
}

class StopsMap extends Component {
  static propTypes = {
    stops: PropTypes.array.isRequired,
  };

  state = {
    markers: [],
    bounds: [],
    alerts: [],
  };

  componentWillMount() {
    this.setState({
      splitpolylines: [],
      service: [],
      fit: 1,
    });
    this.setState({
      markers: mapStopsToMarkers(this.props.stops),
      bounds: L.latLngBounds(mapStopsToMarkers(this.props.stops)),
      polylines: this.props.stops.map((stop, index) => {
        if (stop.geometry_to_next_stop) {
          if (
            !stop.has_stop_been_visited && !stop.has_stop_been_passed) {
            return polyUtil.decode(stop.geometry_to_next_stop);
          }
          return [];
        }
        return [];
      }),
      polylinesvisited: this.props.stops.map((stop, index) => {
        if (
          stop.geometry_to_next_stop &&
          (stop.has_stop_been_visited || stop.has_stop_been_passed)
        ) {
          // eslint-disable-next-line
          if (
            (this.props.tracking 
              && this.props.tracking.is_completed === false) && (this.props.stops.length > index) && !(this.props.stops[index + 1].has_stop_been_visited || this.props.stops[index + 1].has_stop_been_passed)
          ) {
            let poly = polyUtil.decode(stop.geometry_to_next_stop);
            poly = poly.map((x) => x.reverse());
            let newpoly = [];
            let line = turf.lineString(poly);
            let dist = 0.01;
            let length = turf.lineDistance(line, 'kilometers');
            newpoly.push([poly[0][1], poly[0][0]]);
            for (let i = 1; i <= length / dist; i++) {
              let turfPoint = turf.along(line, i * dist, 'kilometers');
              newpoly.push(turfPoint.geometry.coordinates.reverse());
            }
            newpoly.push([poly[poly.length - 1][1], poly[poly.length - 1][0]]);
            poly = newpoly;
            
            dist = 0;
            let dist2 = 0;
            let spoly = [];
            let spoly2 = [];
            let sindex = 0;
            let service;
            for (let i = 0; i < poly.length; i++) {
              if (
                this.props.active_vehicle &&
                this.props.active_vehicle.current_wgs84_latitude_degrees
              ) {
                if (!dist) {
                  dist = turf.distance(
                    [
                      this.props.active_vehicle.current_wgs84_longitude_degrees,
                      this.props.active_vehicle.current_wgs84_latitude_degrees,
                    ],
                    [poly[i][1], poly[i][0]],
                    'kilometers'
                  );
                } else {
                  dist2 = turf.distance(
                    [
                      this.props.active_vehicle.current_wgs84_longitude_degrees,
                      this.props.active_vehicle.current_wgs84_latitude_degrees,
                    ],
                    [poly[i][1], poly[i][0]],
                    'kilometers'
                  );
                  if (dist2 < dist) {
                    dist = dist2;
                    sindex = i;
                  }
                }
              }
            }
            if (dist <= 0.15 && this.props.active_vehicle && this.props.active_vehicle.current_wgs84_longitude_degrees) {
              for (let ii = 0; ii < poly.length; ii++) {
                if (ii < sindex) {
                  spoly.push(poly[ii]);
                } else if (ii === sindex) {
                  service = this.props;
                  if (
                    this.props.stops[index + 1] &&
                    this.props.active_vehicle &&
                    this.props.active_vehicle.distance
                  ) {
                    service.active_vehicle.distance =
                      ((poly.length - sindex) / 10) * 0.621371;
                  } else if (service.active_vehicle) service.active_vehicle.distance = null;
                  service.active_vehicle.current_wgs84_latitude_degrees = poly[ii][0];
                  service.active_vehicle.current_wgs84_longitude_degrees = poly[ii][1];
                  spoly.push(poly[ii]);
                  spoly2.push(poly[ii]);
                } else spoly2.push(poly[ii]);
              }
              this.setState({
                splitpolylines: [spoly2],
                service: service,
              });
              this.forceUpdate();
              return spoly;
            } else {
              this.setState({
                splitpolylines: [poly],
                service: this.props,
              });
              return [];
            }
          }
          return polyUtil.decode(stop.geometry_to_next_stop);
        }
        return [];
      }),
      alerts: mapAlertsToMarkers(this.props.traffic_incidents),
    });
  }

  topFunction() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  handleScroll() {
    if (
      document.body.scrollTop > 20 ||
      document.documentElement.scrollTop > 20
    ) {
      document.getElementById('topBtn').style.display = 'block';
    } else {
      document.getElementById('topBtn').style.display = 'none';
    }
  }

  componentDidMount() {
    window.addEventListener('scroll', this.handleScroll);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  }

  componentDidUpdate(prevProps) {
    if (this.mapRef) {
      if (
        (this.props.fitbounds &&
          prevProps.fitbounds !== this.props.fitbounds) ||
        this.state.fit
      ) {
        this.mapRef.leafletElement.invalidateSize();
        if (this.props.fitbounds) {
          this.mapRef.leafletElement.fitBounds(this.props.fitbounds, {
            padding: [30, 30],
          });
          this.setState({
            fit: 0,
          });
        } else if (this.state.bounds) {
          this.mapRef.leafletElement.fitBounds(this.state.bounds, {
            padding: [30, 30],
          });
          this.setState({
            fit: 0,
          });
        }
      }
      if (
        prevProps.location.pathname !== this.props.location.pathname ||
        prevProps.stops !== this.props.stops
      ) {
        this.setState({
          splitpolylines: [],
          service: [],
        });
        const map = this.mapRef.leafletElement;
        map.invalidateSize();
        this.setState({
          markers:
            this.state.zoom < 9
              ? this.props.stops.map((stop, index) => {
                  if (index === 0) {
                    return {
                      ...stop,
                      icon: createIcon(
                        getIconPathFromActiveTheme('stop-start'),
                        iconBigSize,
                        'svg',
                        [12, 40],
                        [1, -45]
                      ),
                    };
                  } else if (this.props.stops.length === index + 1) {
                    return {
                      ...stop,
                      icon: createIcon(
                        getIconPathFromActiveTheme('stop-finish'),
                        iconBigSize,
                        'svg',
                        [12, 40],
                        [1, -45]
                      ),
                    };
                  } else {
                    return {
                      lat: stop.wgs84_latitude_degrees,
                      lng: stop.wgs84_longitude_degrees,
                      name: stop[THEME.PAGES.DETAILS.STOP]
                        ? stop[THEME.PAGES.DETAILS.STOP]
                        : stop.public_facing_name
                        ? stop.public_facing_name
                        : stop.name,
                      icon: createIcon(
                        getIconPathFromActiveTheme('stop-circle'),
                        iconSmallSize,
                        'svg',
                        [8, 15],
                        [1, -14]
                      ),
                    };
                  }
                })
              : mapStopsToMarkers(this.props.stops),
          bounds: L.latLngBounds(mapStopsToMarkers(this.props.stops)),
          polylines: this.props.stops.map((stop, index) => {
            if (stop.geometry_to_next_stop) {
              if (
                !stop.has_stop_been_visited && !stop.has_stop_been_passed) {
                return polyUtil.decode(stop.geometry_to_next_stop);
              }
              return [];
            }
            return [];
          }),
          polylinesvisited: this.props.stops.map((stop, index) => {
            if (
              stop.geometry_to_next_stop &&
              (stop.has_stop_been_visited || stop.has_stop_been_passed)
            ) {
              // eslint-disable-next-line
              if (
                (this.props.tracking 
                  && this.props.tracking.is_completed === false) && (this.props.stops.length > index) && !(this.props.stops[index + 1].has_stop_been_visited || this.props.stops[index + 1].has_stop_been_passed)
              ) {
                let poly = polyUtil.decode(stop.geometry_to_next_stop);
                poly = poly.map((x) => x.reverse());
                let newpoly = [];
                let line = turf.lineString(poly);
                let dist = 0.01;
                let length = turf.lineDistance(line, 'kilometers');
                newpoly.push([poly[0][1], poly[0][0]]);
                for (let i = 1; i <= length / dist; i++) {
                  let turfPoint = turf.along(line, i * dist, 'kilometers');
                  newpoly.push(turfPoint.geometry.coordinates.reverse());
                }
                newpoly.push([poly[poly.length - 1][1], poly[poly.length - 1][0]]);
                poly = newpoly;
                
                dist = 0;
                let dist2 = 0;
                let spoly = [];
                let spoly2 = [];
                let sindex = 0;
                let service;
                for (let i = 0; i < poly.length; i++) {
                  if (
                    this.props.active_vehicle &&
                    this.props.active_vehicle.current_wgs84_latitude_degrees
                  ) {
                    if (!dist) {
                      dist = turf.distance(
                        [
                          this.props.active_vehicle.current_wgs84_longitude_degrees,
                          this.props.active_vehicle.current_wgs84_latitude_degrees,
                        ],
                        [poly[i][1], poly[i][0]],
                        'kilometers'
                      );
                    } else {
                      dist2 = turf.distance(
                        [
                          this.props.active_vehicle.current_wgs84_longitude_degrees,
                          this.props.active_vehicle.current_wgs84_latitude_degrees,
                        ],
                        [poly[i][1], poly[i][0]],
                        'kilometers'
                      );
                      if (dist2 < dist) {
                        dist = dist2;
                        sindex = i;
                      }
                    }
                  }
                }
                if (dist <= 0.15 && this.props.active_vehicle && this.props.active_vehicle.current_wgs84_longitude_degrees) {
                  for (let ii = 0; ii < poly.length; ii++) {
                    if (ii < sindex) {
                      spoly.push(poly[ii]);
                    } else if (ii === sindex) {
                      service = this.props;
                      if (
                        this.props.stops[index + 1] &&
                        this.props.active_vehicle &&
                        this.props.active_vehicle.distance
                      ) {
                        service.active_vehicle.distance =
                          ((poly.length - sindex) / 10) * 0.621371;
                      } else if (service.active_vehicle) service.active_vehicle.distance = null;
                      service.active_vehicle.current_wgs84_latitude_degrees = poly[ii][0];
                      service.active_vehicle.current_wgs84_longitude_degrees = poly[ii][1];
                      spoly.push(poly[ii]);
                      spoly2.push(poly[ii]);
                    } else spoly2.push(poly[ii]);
                  }
                  this.setState({
                    splitpolylines: [spoly2],
                    service: service,
                  });
                  this.forceUpdate();
                  return spoly;
                } else {
                  this.setState({
                    splitpolylines: [poly],
                    service: this.props,
                  });
                  return [];
                }
              }
              return polyUtil.decode(stop.geometry_to_next_stop);
            }
            return [];
          }),
          alerts: mapAlertsToMarkers(this.props.traffic_incidents),
        });
        setTimeout(() => {
          this.centerPolyline();
        }, 100);
      }
    }
  }

  handleZoom = (e) => {
    const zoom = e.target._zoom;

    this.setState({
      zoom,
    });

    if (zoom < 9) {
      this.setState({
        markers: this.props.stops.map((stop, index) => {
          if (index === 0) {
            return {
              ...stop,
              icon: createIcon(
                getIconPathFromActiveTheme('stop-start'),
                iconBigSize,
                'svg',
                [12, 40],
                [1, -45]
              ),
            };
          } else if (this.props.stops.length === index + 1) {
            return {
              ...stop,
              icon: createIcon(
                getIconPathFromActiveTheme('stop-finish'),
                iconBigSize,
                'svg',
                [12, 40],
                [1, -45]
              ),
            };
          } else {
            return {
              lat: stop.wgs84_latitude_degrees,
              lng: stop.wgs84_longitude_degrees,
              name: stop[THEME.PAGES.DETAILS.STOP]
                ? stop[THEME.PAGES.DETAILS.STOP]
                : stop.public_facing_name
                ? stop.public_facing_name
                : stop.name,
              icon: createIcon(
                getIconPathFromActiveTheme('stop-circle'),
                iconSmallSize,
                'svg',
                [8, 15],
                [1, -14]
              ),
            };
          }
        }),
      });
    } else {
      this.setState({
        markers: mapStopsToMarkers(this.props.stops),
      });
    }
  };

  centerPolyline = () => {
    const { bounds } = this.state;
    if (bounds && this.mapRef) {
      this.mapRef.leafletElement.invalidateSize();
    }
  };

  responsiveMarker = (ref) => {
    if (ref) {
      ref.target._popup._wrapper.classList.add('alert-popup');
    }
  };

  render() {
    const {
      markers,
      bounds,
      polylines,
      polylinesvisited,
      splitpolylines,
      alerts,
    } = this.state;


    let {
      service
    } = this.state;

    //if (!service.active_vehicle)
    service = this.props;
    //if (this.props.active_vehicle)
      //service.active_vehicle = this.props.active_vehicle;
    return (
      <MapWithProvider
        fullscreenControl
        bounds={this.props.fitbounds ? this.props.fitbounds : bounds}
        boundsOptions={this.props.fitbounds ? boundsOptions : null}
        onZoom={this.handleZoom}
        onViewreset={this.handleZoom}
        ref={(ref) => (this.mapRef = ref)}
        path={(CLIP_PADDING) => 1}
        maxZoom={18}
      >
        {false && FullscreenControl}

        {markers.map((marker) => (
          <Fragment key={v4()}>
            <Marker position={marker.lat ? marker : [marker.wgs84_latitude_degrees, marker.wgs84_longitude_degrees]} icon={marker.icon}>
              <Popup>
                {getStopName(marker.name)[0]}
                <br />
                {getStopName(marker.name).length > 1
                  ? getStopName(marker.name)[1]
                  : null}
              </Popup>
            </Marker>
          </Fragment>
                ))}

        {!!alerts.length &&
          alerts.map((alert) => (
            <Fragment key={v4()}>
              <Marker position={alert} icon={alert.icon}>
                <Popup className="alert-popup">
                  <p>
                    <strong>{alert.name}</strong>
                  </p>
                  <div>{alert.description}</div>
                  <div>Cleared: {alert.cleared}</div>
                  <div></div>
                  {alert.updated && (
                    <div>Last updated: {getLastUpdate(alert) + ' ago'}</div>
                  )}
                </Popup>
              </Marker>
            </Fragment>
          ))}

        {/*{service && service.live && (
          <Fragment key={v4()}>
            <Marker
              position={service.live}
              icon={createIcon(
                getIconPathFromActiveTheme('bus-icon-map'),
                [28, 28],
                'svg',
                [14, 14],
                [1, -13]
              )}
            >
              <Popup className="alert-popup">
                <p>
                  <span className="vehicle-route">{service.route}</span>
                  <span className="vehicle-time">
                    {getTime(service.startTime)}
                  </span>
                </p>

                <h3>{`${service.depart} → ${service.arrival}`}</h3>
                <div>
                  {service.live.status === 0
                    ? `Currently parked at ${service.live.geoLocation}`
                    : `Currently moving along ${service.live.geoLocation}`}
                </div>
                {service.live.distance && (
                  <div className="vehicle-distance">{`Distance to next stop: ${service.live.distance.toFixed(
                    1
                  )} miles`}</div>
                )}
                <p>
                  <span className="vehicle-update-time">{`Last updated: ${getTimeSec(
                    service.live.timestamp
                  )}`}</span>
                </p>
              </Popup>
            </Marker>
          </Fragment>
                  )}*/}

        {THEME.REALTIME && service.active_vehicle &&
          service.active_vehicle.current_wgs84_latitude_degrees && !service.tracking.is_completed && (
            <Fragment key={v4()}>
              <Marker
                position={[service.active_vehicle.current_wgs84_latitude_degrees, service.active_vehicle.current_wgs84_longitude_degrees]}
                icon={createIcon(
                  getIconPathFromActiveTheme('bus-icon-map'),
                  [28, 28],
                  'svg',
                  [14, 14],
                  [1, -13]
                )}
              >
                <Popup className="alert-popup">
                  <p>
                    <span className="vehicle-route">
                      {this.props.trip.route_id}
                    </span>
                    <span className="vehicle-time">
                      {getTime(this.props.trip.departure_time_formatted_local)}
                    </span>
                  </p>

                  <h3>{`${this.props.trip.departure_location_name} → ${this.props.trip.arrival_location_name}`}</h3>
                  {service.coachtracker &&
                      service.coachtracker.status_description &&
                        <p>
                            {service.coachtracker.status_description}
                        </p>
                  }
                  {service.coachtracker.distance_to_next_stop_km && (
                    <div className="vehicle-distance">{`Distance to next stop: ${THEME.LOCALE === 'UK' || THEME.DISTANCE === 'M' ? (service.coachtracker.distance_to_next_stop_km * 0.621371).toFixed(1) + ' miles' : service.coachtracker.distance_to_next_stop_km.toFixed(1) + ' km'}`}</div>
                  )}

                  <span className="vehicle-update-time">{`Last updated: ${getTimeSec(
                    service.active_vehicle.last_update_time_formatted_local
                  )}`}</span>
                </Popup>
              </Marker>
            </Fragment>
          )}

        <Polyline
          positions={polylines}
          pathOptions={{ color: "red" }}
          weight={7}
          opacity={1}
        />

        {splitpolylines && (
          <Polyline
            positions={splitpolylines}
            pathOptions={{ color: "red" }}
            weight={7}
            opacity={1}
          />
        )}

        <Polyline
          positions={polylinesvisited}
          pathOptions={{ color: "red" }}
          weight={7}
          opacity={1}
        />

        <img
          id="topBtn"
          src={iconScrollTop}
          alt="Top"
          className="scrol-top"
          style={{ display: 'none' }}
          onClick={this.topFunction}
        />
      </MapWithProvider>
    );
  }
}

export default withRouter(connect(mapStateToProps, {})(StopsMap));



// WEBPACK FOOTER //
// ./src/containers/service-list/StopsMap.js